import {defineStore} from "pinia";
import {CopySelection} from "@/models/CopySelection.model";
import {Interval} from "luxon";
import { useClassroomsStore } from "@/stores/Classrooms.store.ts";

interface ClipboardStoreState {
	clipboard: CopySelection[]
}

export const useClipboardStore = defineStore('clipboard', {
	state: (): ClipboardStoreState => {
		const clipboard: CopySelection[] = [];

		// Load from localStorage
		try {
			const clipboardData = localStorage.getItem('clipboard');
			if (clipboardData) {
				const data = JSON.parse(clipboardData);
				data.forEach((copySelection: any) => {
					clipboard.push(CopySelection.deserialize(copySelection));
				});
			}
		} catch (e) {
			console.error(e);
		}

		return {
			clipboard: clipboard
		};
	},

	getters: {
		availableClipboardItems(state) {
			return state.clipboard.filter(
				(copySelection: CopySelection) => {
					return copySelection.isAvailable;
				}
			)
		}
	},

	actions: {
		copy(interval: Interval, themeNames: string[] | null = null) {
			const copySelection = new CopySelection(
				interval,
				themeNames ?? [],
				useClassroomsStore().getCurrentClassroom().id,
			);

			// Make sure it's not in the board already
			const existingIndex = this.clipboard.findIndex(item => {
				return item.interval.start.toString() === copySelection.interval.start.toString()
					&& item.interval.end.toString() === copySelection.interval.end.toString()
					&& item.classroomId === copySelection.classroomId;
			});

			if (existingIndex >= 0) {
				this.clipboard.splice(existingIndex, 1);
			}

			this.clipboard.push(copySelection);
			this.storeInLocalStorage();
		},

		clear() {
			this.clipboard = [];
			this.storeInLocalStorage();
		},

		remove(copySelection: CopySelection) {
			const index = this.clipboard.indexOf(copySelection);
			if (index >= 0) {
				this.clipboard.splice(index, 1);
			}
			this.storeInLocalStorage();
		},

		storeInLocalStorage() {
			try {
				const data: any[] = [];
				this.clipboard.forEach((copySelection) => {
					data.push(copySelection.serialize());
				});
				localStorage.setItem('clipboard', JSON.stringify(data));
			} catch (e) {
				console.error(e);
			}
		}
	}
});
